import { ImageParams } from 'components/Common/ResponsiveImage'
import { ImageChangeHandler, ImageCarouselRefInterface, ImageLoadHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import React, { PropsWithChildren, ReactElement, useCallback, useRef } from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const Container = styled.div``

type ImageCarouselProps = React.ComponentProps<typeof ImageCarousel>

interface Props extends PropsWithChildren {
  bookmarkAction?: ReactElement
  images: Array<App.Image>
  productLabel?: ReactElement | false
  imageParams: ImageParams,
  onImageChange?: ImageChangeHandler;
  onImageLoad?: ImageLoadHandler;
  imageCarouselProps?: Omit<ImageCarouselProps, 'images' | 'carouselRef' | 'topLeft' | 'topRight' | 'arrowBehaviour' | 'images' | 'onImageChange' | 'onImageLoad'>,
}

function OfferImageCarouselWrapper(props: Props) {
  const {
    bookmarkAction,
    images,
    productLabel,
    imageParams,
    onImageChange,
    onImageLoad,
    imageCarouselProps,
  } = props
  const carouselRef = useRef<ImageCarouselRefInterface | null>(null)

  const isInHoverState = useRef(false) // Are we in hover-induced state?
  const isDesktop = useScreenSizeUp('tablet')
  const isHoverEnabled = isDesktop

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    if (image) {
      // reset hover state if the image is not the first one
      if (isInHoverState.current && index != 1) {
        isInHoverState.current = false
      }
    }
    onImageChange?.(index, image)
  }, [onImageChange])

  const handleMouseEnter = useCallback(() => {
    // Only trigger hover if on first image and we have a second image
    if (isHoverEnabled && images.length > 1 && carouselRef.current?.getCurrentImageIndex() === 0) {
      isInHoverState.current = true
      carouselRef.current.goToImage(images[1], 'smooth')
    }
  }, [isHoverEnabled, images, carouselRef])

  const handleMouseLeave = useCallback(() => {
    // Only reset if we're still in hover state (user didn't manually interact)
    if (isHoverEnabled && isInHoverState.current && carouselRef.current) {
      carouselRef.current?.goToImage(images[0], 'smooth')
    }

    // Clean up hover state
    isInHoverState.current = false
  }, [isHoverEnabled, images, carouselRef])

  return <Container
    className={OfferImageCarouselWrapper.name}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
  >
    <ImageCarousel
      {...imageCarouselProps}
      carouselRef={carouselRef}
      topLeft={productLabel}
      topRight={bookmarkAction ? <TripGuard>{bookmarkAction}</TripGuard> : undefined}
      images={images}
      arrowBehaviour="interaction-visible"
      imageParams={imageParams}
      onImageChange={handleImageChange}
      onImageLoad={onImageLoad}
    />
  </Container>
}

export default OfferImageCarouselWrapper
